import type { DialpadInputProps } from './index.types';
import { useSoftphoneDialpad } from 'Modules/softphone/hooks/useSoftphoneDialpad';
import React from 'react';
import { Icon } from '~/components/shared/Icon';
import { cn } from '~/lib/utils';

/**
 * DialpadInput Component
 * Editable input field for entering phone numbers before call initiation
 */
export const DialpadInput = ({
  placeholder = 'Enter a number to start a call.',
  testID,
  className,
}: DialpadInputProps): React.ReactElement => {
  const {
    dialpadTextFieldRef,
    dialpadDigits,
    handleChange,
    handleKeyDown,
    handleSelect,
    handleBlur,
    handlePaste,
    handleBackspace,
  } = useSoftphoneDialpad();

  const dialpadInputTestId = testID ? `${testID}-dialpad-input` : 'dialpad-input';

  return (
    <div
      className="w-full flex items-center justify-between gap-[8px] py-[4px] h-[22px] leading-[22px]"
      data-testid={`${dialpadInputTestId}-container`}
    >
      <input
        ref={dialpadTextFieldRef}
        type="text"
        dir="ltr"
        value={dialpadDigits}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onSelect={handleSelect}
        onBlur={handleBlur}
        onPaste={handlePaste}
        placeholder={placeholder}
        className={cn(
          'w-full min-w-0',
          'bg-transparent transition-colors outline-none',
          'text-[16px] font-normal text-foreground text-left tracking-wide',
          'placeholder:text-[16px] placeholder-[#B3B3B3] placeholder:text-left',
          'selection:bg-primary selection:text-primary-foreground',
          'focus-visible:outline-none',
          'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        data-testid={`${dialpadInputTestId}-input-value`}
        aria-label="Phone number input"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        autoFocus
      />

      {dialpadDigits && dialpadDigits.length > 0 && (
        <button
          type="button"
          onClick={handleBackspace}
          className={cn(
            'cursor-pointer',
            'flex-shrink-0 size-[24px] rounded-[4px]',
            'flex items-center justify-center',
            'text-muted-foreground',
            'transition-colors',
            'focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-ring/50'
          )}
          data-testid={`${dialpadInputTestId}-backspace`}
          aria-label="Backspace"
        >
          <Icon icon="backspace" size="medium" />
        </button>
      )}
    </div>
  );
};
